import * as kinds from '@nostr/tools/kinds'
import { HallwayUniverse } from './types/universe'

function mergeUniverse<T extends Record<string, any>>(
  defaults: T,
  provided: Partial<T> | undefined
): T {
  const out = { ...defaults }
  if (provided) {
    for (const key of Object.keys(provided) as (keyof T)[]) {
      if (provided[key] !== undefined) {
        out[key] = provided[key]!
      }
    }
  }
  return out
}

const universeDefaults: HallwayUniverse = {
  indexerUrls: [
    'wss://purplepag.es',
    'wss://indexer.coracle.social',
    'wss://indexer.nostrarchives.com',
    'wss://user.kindpag.es'
  ],
  hardcodedFeeds: [
    {
      name: 'Basically Global',
      relays: ['wss://theforest.nostr1.com/', 'wss://nostr.wine/', 'wss://pyramid.fiatjaf.com/']
    },
    {
      name: 'Holoboard',
      relays: ['wss://relay.holoboard.space']
    },
    {
      name: 'Kinda Trending',
      relays: [
        'wss://trending.relays.land',
        'wss://antiprimal.net/hot',
        'wss://pyramid.fiatjaf.com/uppermost',
        'wss://feeds.nostrarchives.com/notes/trending/likes/today',
        'wss://feeds.nostrarchives.com/notes/trending/reposts/today'
      ]
    },
    {
      name: 'Livestreams',
      icon: 'https://blossom.jumble.social/ea298368e18e13a489ba49dc7ea91a2c5278f8fc5ec0cd9f9def536086ed9062.png',
      relays: ['wss://livestream.livelier.live'],
      kinds: [1, 30311]
    },
    { name: 'Aquatic Chicken Theory', relays: ['wss://aquaticchickentheory.nostr1.com/'] },
    { name: '_', relays: ['wss://spatia-arcana.com/nox', 'wss://relays.land/spatianostra'] }
  ],
  defaultFavoriteRelays: [
    'wss://theforest.nostr1.com/',
    'wss://pyramid.fiatjaf.com/popular',
    'wss://spatia-arcana.com/lux',
    'wss://news.utxo.one/'
  ],
  defaultBlossomServers: [
    'https://blossom.primal.net/',
    'https://nostr.media/',
    'https://nostr.download/',
    'https://blossom.band/'
  ],
  defaultInbox: ['wss://nostr.mom', 'wss://nostr.mom', 'wss://inbox.relays.land'],
  defaultOutbox: ['wss://nos.lol', 'wss://offchain.pub', 'wss://relay.wisp.talk'],
  baseShareURL: 'https://njump.me/',
  clientHandlers: {
    byKind: {
      [kinds.LongFormArticle]: [
        { name: 'Habla', urlPattern: 'https://habla.coracle.social/a/{}' },
        { name: 'Yakihonne', urlPattern: 'https://yakihonne.com/{}' }
      ],
      [kinds.LiveEvent]: [
        { name: 'zap.stream', urlPattern: 'https://zap.stream/{}' },
        { name: 'Shosho', urlPattern: 'https://shosho.live/live/{}' }
      ],
      [kinds.GroupMetadata]: [
        { name: 'Nostrord', urlPattern: 'https://web.nostrord.com/?relay={hint}&group={d}' }
      ],
      [kinds.Date]: [{ name: 'Coracle', urlPattern: 'https://coracle.social/{}' }],
      [kinds.Time]: [{ name: 'Coracle', urlPattern: 'https://coracle.social/{}' }]
    },
    fallback: [
      {
        name: 'Grimoire',
        urlPattern: 'https://grimoire2.netlify.app/run?cmd=open%20{}'
      },
      {
        name: 'njump',
        urlPattern: 'https://njump.me/{}'
      }
    ]
  },
  defaultNostrConnectRelays: ['wss://bucket.coracle.social/', 'wss://relay.primal.net/'],
  bigRelayUrls: ['wss://relay.damus.io/', 'wss://relay.primal.net/', 'wss://nos.lol/'],
  noteSearchRelayUrls: ['wss://search.nostrarchives.com/'],
  noteSearchRelayUrlsExtra: ['wss://antiprimal.net', 'wss://nostr.wine', 'wss://search.nos.today'],
  profileSearchRelayUrls: ['wss://relay.vertexlab.io', 'wss://search.nostrarchives.com/'],
  registryOfKindsURL:
    'https://cdn.jsdelivr.net/gh/nostr-protocol/registry-of-kinds@master/schema.yaml',
  appName: 'Hallway',
  groupedTimeframe: 60 * 60 * 24,
  maxEmbedDepth: 2,
  groupedDefault: false,
  defaultPrimaryColor: 'TEAL',
  defaultThemeSetting: 'system',
  includeRepliesDefault: true,
  protectedDefault: false,
  hideNotificationsDefault: false,
  hideConversationsDefault: false,
  hideFollowingDefault: false,
  hideFavoriteRelaysDefault: false,
  enableNipA3: false,
  notificationListStyleDefault: 'compact',
  noteListStyleDefault: 'full',
  sidebarCollapseDefault: false,
  enableSingleColumnLayoutDefault: false,
  readRepliesFrom: [],
  readRepliesFromExclusive: false,
  dearrowYoutube: true,
  linkPreviewService: 'https://dufflepud.onrender.com',
  genericCorsProxy: 'https://cors-proxy.jumble.social/sites/',
  kindGroups: [
    {
      kinds: [kinds.Voice, kinds.ShortTextNote, kinds.Repost, kinds.GenericRepost],
      label: 'Notes'
    },
    { kinds: [kinds.Comment, kinds.VoiceComment], label: 'Comments' },
    { kinds: [kinds.Highlights], label: 'Highlights' },
    { kinds: [kinds.WikiArticle], label: 'Wikis' },
    { kinds: [kinds.WebBookmarks], label: 'Bookmarks' },
    { kinds: [kinds.LongFormArticle], label: 'Articles', disabled: true },
    {
      kinds: [kinds.Photo, kinds.NormalVideo, kinds.ShortVideo, kinds.PodcastEpisode],
      label: 'Media',
      disabled: true
    }
  ],
  homeMarkdown:
    '# Hallway\n\n' +
    'This is a demo of **Hallway**, a Nostr client made to be customized for communities.\n\n' +
    'Customizations are focused on the relays the community reads from or talk to in general, but also include appearance, branding, translations (or you can just change what one or more labels on the site say), default Nostr settings for new users and many other small things.\n\n' +
    'The idea is that a specific group of people will customize it so that new members can find a familiar environment when they first join, with content curated to their immediate interest and perhaps even isolated from the external world and its dangers. Could be a neighborhood association, a sports fan community, a church. Or it could just be a custom client that normal Nostr users go to when they want to see specific types of content, with the curation done by the people who manage that Hallway instance and associated relays, without these users having to do the customizations themselves in more flexible clients, for example there could be a client optimized for content about some sport, or about hamters.\n\n' +
    'Anyone can deploy their own version of Hallway and it is basically free to run, as it is only a static site. There are many ways to do so, but one easy way to deploy your custom version of Hallway can be found at https://hallway-deployer.fiatjaf.com/, it works by publishing it to your Netlify account.\n\n' +
    '### This section\n\n' +
    'This section (and anything above too, to be clear) can be filled with anything you want. It supports Markdown even though Markdown is not great.\n\n',
  pomegranateCentral: 'https://auth.njump.me',
  pomegranateOperators: [
    'https://po.njump.me',
    'https://po.f7z.io',
    'https://po.jumble.social',
    'https://po.coracle.social',
    'https://po.yakihonne.com'
  ],
  avatarStyleDefault: 'square'
} satisfies HallwayUniverse

window.hallway = {
  ...window.hallway,
  universe: mergeUniverse(
    universeDefaults,
    window.hallway?.universe as Partial<HallwayUniverse> | undefined
  )
}

export const feedKinds = (window.hallway.universe.kindGroups ?? []).flatMap((g) => g.kinds)
